/** 时间轴分镜稿：0-3秒：运镜/动作/台词…，参考说明 */

export const STORYBOARD_TIMELINE_FORMAT_EXAMPLE = `0-3秒：中景女主转身坐下，转镜头，女主下了一步棋子，并说「你输了」，参考的分镜画面，女主声音参考中的御姐音色，背景参考。
3-4秒：快速摇镜头，转向对面特写中的男人面部，男人说：「怎么会！」，参考的分镜画面，男人咬牙切齿，对结果很不满。
4-6秒：切镜头，俯拍，女人下了一步棋，对面的人们惊叹，参考的分镜画面。
6-8秒：镜头迅速向下摇，画面黑屏转场，后画面渐亮，昏暗室内，女人看着窗外月色静静地说「我们走着瞧」，参考的分镜画面。`

const TIMELINE_SEGMENT_RE =
  /^(\d+(?:\.\d+)?)\s*[-~–—至到]\s*(\d+(?:\.\d+)?)\s*秒\s*[：:]\s*(.+)$/i

export function looksLikeTimelineStoryboard(text) {
  return TIMELINE_SEGMENT_RE.test(String(text || ''))
}

export function extractDialogueFromTimelineSegment(body) {
  const s = String(body || '')
  const m1 = /(?:说|说道|喊道|低声说|静静地说|旁白)[：:]\s*[「『"'“]([^」』"'”]+)[」』"'”]/u.exec(s)
  if (m1) return m1[1].trim()
  const m2 = /[「『]([^」』]+)[」』]/u.exec(s)
  return m2 ? m2[1].trim() : ''
}

/** 从整理稿/模型文本解析时间轴段落（JSON 失败时的回退） */
export function parseTimelineStoryboardSegments(text) {
  const src = String(text || '').trim()
  if (!src) return []
  const re = new RegExp(TIMELINE_SEGMENT_RE.source, 'gim')
  const shots = []
  let m
  while ((m = re.exec(src)) !== null) {
    const start = parseFloat(m[1])
    const end = parseFloat(m[2])
    let durationSec = Math.round(end - start)
    if (!Number.isFinite(durationSec) || durationSec < 1) durationSec = 1
    durationSec = Math.min(15, durationSec)
    const body = String(m[3] || '')
      .trim()
      .replace(/\s+/g, ' ')
    const dialogue = extractDialogueFromTimelineSegment(body) || '无'
    shots.push({
      shotNo: String(shots.length + 1).padStart(2, '0'),
      durationSec,
      timeStartSec: Number.isFinite(start) ? start : undefined,
      timeEndSec: Number.isFinite(end) ? end : undefined,
      jingbie: '—',
      yunjing: '—',
      composition: body,
      dialogue,
      sfx: '无',
      videoPrompt: `${m[1]}-${m[2]}秒：${body}`,
      charIndices: [],
      propIndices: [],
      sceneIndices: []
    })
  }
  return shots
}

export function buildTimelineStyleShotBody(shot) {
  const s = shot || {}
  const parts = []
  const j = String(s.jingbie || '').trim()
  const y = String(s.yunjing || '').trim()
  const c = String(s.composition || '').trim()
  if (j && j !== '—') parts.push(j)
  if (y && y !== '—' && y !== j) parts.push(y)
  if (c && c !== '—') parts.push(c)
  const dia = String(s.dialogue || '').trim()
  if (dia && dia !== '无') {
    parts.push(/说|旁白|道/.test(dia) ? dia : `并说「${dia}」`)
  }
  if (!/参考的分镜画面/.test(parts.join(''))) parts.push('参考的分镜画面')
  const sfx = String(s.sfx || '').trim()
  if (sfx && sfx !== '无') parts.push(sfx)
  else if (!/背景参考/.test(parts.join(''))) parts.push('背景参考')
  return parts.filter(Boolean).join('，')
}

/** 将镜头数组格式化为时间轴分镜正文 */
export function formatShotsAsTimelineStoryboard(shots) {
  let cursor = 0
  const lines = []
  for (const s of shots || []) {
    const dur = Math.min(15, Math.max(1, Number(s.durationSec) || 5))
    let start =
      s.timeStartSec != null && Number.isFinite(Number(s.timeStartSec))
        ? Number(s.timeStartSec)
        : cursor
    let end =
      s.timeEndSec != null && Number.isFinite(Number(s.timeEndSec))
        ? Number(s.timeEndSec)
        : start + dur
    if (end <= start) end = start + dur
    cursor = end
    const vp = String(s.videoPrompt || '').trim()
    const firstLine = vp.split(/\r?\n/)[0].trim()
    const embedded = TIMELINE_SEGMENT_RE.exec(firstLine)
    if (embedded) {
      lines.push(firstLine)
      continue
    }
    const body = vp || buildTimelineStyleShotBody(s)
    const bodyNoPrefix = body.replace(/^\d+(?:\.\d+)?\s*[-~–—至到]\s*\d+(?:\.\d+)?\s*秒\s*[：:]\s*/i, '')
    lines.push(`${formatTimelineSeconds(start)}-${formatTimelineSeconds(end)}秒：${bodyNoPrefix}`)
  }
  return lines.join('\n')
}

function formatTimelineSeconds(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return '0'
  return Math.abs(x - Math.round(x)) < 1e-6 ? String(Math.round(x)) : String(x)
}

/** 解析后生成视频节点：单节点时长目标区间（秒，含边界） */
export const STORYBOARD_VIDEO_NODE_MIN_SEC = 10
export const STORYBOARD_VIDEO_NODE_MAX_SEC = 15

function clampShotDurationSec(d) {
  const n = Math.round(Number(d))
  if (!Number.isFinite(n) || n < 1) return 1
  return Math.min(STORYBOARD_VIDEO_NODE_MAX_SEC, n)
}

function unionStoryboardIndexLists(lists) {
  const s = new Set()
  for (const arr of lists || []) {
    if (!Array.isArray(arr)) continue
    for (const x of arr) {
      if (x == null || x === '') continue
      const n = Number(x)
      if (Number.isFinite(n)) s.add(n)
    }
  }
  return [...s].sort((a, b) => a - b)
}

/** 将多条解析镜头合并为一条视频节点镜头 */
export function mergeStoryboardShotsForVideoNode(parts, totalDur) {
  const list = (parts || []).filter(Boolean)
  if (!list.length) return null
  const durationSec = clampShotDurationSec(totalDur)
  if (list.length === 1) {
    return { ...list[0], durationSec, _sourceParts: [list[0]] }
  }
  const first = list[0]
  const last = list[list.length - 1]
  const sn0 = String(first.shotNo != null ? first.shotNo : '01').trim()
  const sn1 = String(last.shotNo != null ? last.shotNo : sn0).trim()
  const shotNo = sn0 === sn1 ? sn0 : `${sn0}-${sn1}`
  const videoPrompt = list
    .map(p => String(p.videoPrompt || '').trim())
    .filter(Boolean)
    .join('\n')
  const dialogues = list
    .map(p => String(p.dialogue || '').trim())
    .filter(d => d && d !== '无')
  const yunjings = list.map(p => String(p.yunjing || '').trim()).filter(y => y && y !== '—')
  const compositions = list
    .map(p => String(p.composition || '').trim())
    .filter(c => c && c !== '—')
  const sfx = list.map(p => String(p.sfx || '').trim()).find(s => s && s !== '无') || '无'
  const draft = {
    shotNo,
    durationSec,
    jingbie: first.jingbie,
    yunjing: yunjings.length ? yunjings.join('→') : first.yunjing,
    composition: compositions.length ? compositions.join('；') : first.composition,
    dialogue: dialogues.length ? dialogues.join(' / ') : '无',
    sfx
  }
  return {
    ...first,
    ...draft,
    timeStartSec: first.timeStartSec,
    timeEndSec: last.timeEndSec,
    videoPrompt: videoPrompt || buildTimelineStyleShotBody(draft),
    charIndices: unionStoryboardIndexLists(list.map(p => p.charIndices)),
    propIndices: unionStoryboardIndexLists(list.map(p => p.propIndices)),
    sceneIndices: unionStoryboardIndexLists(list.map(p => p.sceneIndices)),
    _sourceParts: list
  }
}

function expandShotsExceedingMaxSec(sh) {
  const total = clampShotDurationSec(sh.durationSec)
  if (total <= STORYBOARD_VIDEO_NODE_MAX_SEC) {
    return [{ ...sh, durationSec: total }]
  }
  const chunks = []
  let start = Number.isFinite(Number(sh.timeStartSec)) ? Number(sh.timeStartSec) : null
  let rem = total
  let part = 0
  while (rem > 0) {
    const chunk = Math.min(STORYBOARD_VIDEO_NODE_MAX_SEC, rem)
    const end = start != null ? start + chunk : undefined
    const baseNo = String(sh.shotNo != null ? sh.shotNo : '01')
    chunks.push({
      ...sh,
      shotNo: part ? `${baseNo}·${part + 1}` : baseNo,
      durationSec: chunk,
      timeStartSec: start != null ? start : sh.timeStartSec,
      timeEndSec: end
    })
    if (start != null) start += chunk
    rem -= chunk
    part += 1
  }
  return chunks
}

/**
 * 将解析镜头合并/拆分为每个视频节点 10～15 秒（不足 10 秒的尾段可单独成镜）
 */
export function groupStoryboardShotsForVideoNodes(shots) {
  const expanded = []
  for (const sh of shots || []) {
    expanded.push(...expandShotsExceedingMaxSec(sh))
  }
  if (!expanded.length) return []

  const out = []
  let buf = []
  let bufDur = 0

  const flushBuf = () => {
    if (!buf.length) return
    const merged = mergeStoryboardShotsForVideoNode(buf, bufDur)
    if (merged) out.push(merged)
    buf = []
    bufDur = 0
  }

  for (const sh of expanded) {
    const dur = clampShotDurationSec(sh.durationSec)
    if (bufDur > 0 && bufDur + dur > STORYBOARD_VIDEO_NODE_MAX_SEC) {
      flushBuf()
    }
    buf.push({ ...sh, durationSec: dur })
    bufDur += dur
    if (bufDur >= STORYBOARD_VIDEO_NODE_MIN_SEC) {
      flushBuf()
    }
  }

  if (buf.length) {
    if (bufDur < STORYBOARD_VIDEO_NODE_MIN_SEC && out.length) {
      const last = out.pop()
      const lastParts = last._sourceParts || [last]
      const merged = mergeStoryboardShotsForVideoNode([...lastParts, ...buf], last.durationSec + bufDur)
      if (merged) out.push(merged)
    } else {
      flushBuf()
    }
  }

  return out.map(({ _sourceParts, ...rest }) => rest)
}

export function buildStoryboardTimelineFormatHint(isEnglish) {
  if (isEnglish) {
    return [
      'Output format: timeline storyboard prose (not tables).',
      'Each paragraph starts with "Start-End sec:" (e.g. 0-3 sec:).',
      'Describe camera moves, blocking, dialogue ("character says: ..."), and end with reference notes like "reference storyboard frame", "voice reference", "background reference".',
      'Single segment 1–15 seconds; timeline must be continuous.',
      'Example:\n' + STORYBOARD_TIMELINE_FORMAT_EXAMPLE.replace(/秒/g, ' sec').replace(/：/g, ': ')
    ].join('\n')
  }
  return [
    '输出格式：时间轴分镜稿（非表格、非镜号列表）。',
    '每一段以「起始秒-结束秒：」起句（如 0-3秒：、3-4秒：），单段时长 1～15 秒，全片时间轴连贯。',
    '段内写清运镜/机位、主体动作、对白（角色说：「…」），段末用逗号串联「参考的分镜画面」「声音/音色参考」「背景参考」等说明。',
    '格式示例：\n' + STORYBOARD_TIMELINE_FORMAT_EXAMPLE
  ].join('\n')
}
